/**
 * Escena del MONTE (canon Adri 2026-07-11, ver `docs/biblia/escenarios.md`):
 * camino de tierra entre pinos con 3 casas dispersas a distinta altura.
 *
 * Mecánica por casa (canon):
 *  - **Casa del VENTURA** y **Casa del BAYONGA**: PROHIBIDO ir. Al tocarlas
 *    se abre un panel de aviso; si el jugador insiste con el botón
 *    "Acercarse igualmente…", un vecino llama a la policía, le detienen,
 *    pierde el 20% del dinero como multa (`MULTA_PORCENTAJE`) y vuelve
 *    al panel de zona (canon GTA / "Busted", ver escenarios.md).
 *  - **Casa RANDOM**: aquí arranca la Misión 1 ("La casa random"). Si la
 *    misión NO está completada, hay un botón "Inspeccionar la casa
 *    random" que lanza `MissionScene` con `{ missionId: 'la-casa-random' }`.
 *    Si está completada, se muestra el texto canon "Ya le disteis un
 *    buen repaso ✅" en lugar del botón (mismo patrón que el sticker del
 *    INTERIOR).
 *
 * Separación dura: textos en `src/data/zonas-actividad.ts`. Esta escena
 * solo dibuja, aplica la multa y enruta.
 */

import Phaser from 'phaser';
import { GameState } from '@/engine/GameState';
import {
  ensureRunning,
  goTo,
  SCENE_KEYS,
  type MissionSceneData,
} from '@/engine/nav';
import {
  AVISO_PROHIBIDO,
  BOTON_INSPECCIONAR_CASA_RANDOM,
  BOTON_INSISTIR,
  BOTON_OK_DETENCION,
  BOTON_VOLVER_AVISO,
  BOTON_VOLVER_BARRIO,
  CASA_RANDOM_COMPLETADA,
  DETENCION,
  DESCRIPCION_MONTE,
  ETIQUETAS_CASAS_MONTE,
  MULTA_PORCENTAJE,
  type CasaMonteId,
} from '@/data/zonas-actividad';

// Paleta coherente con el resto de escenas (pino + tarde).
const COLOR_FONDO_CIELO = 0x8fb8d8;
const COLOR_CIELO_BAJO = 0xd2b48c;
const COLOR_SUELO = 0x7a5a32;
const COLOR_HIERBA = 0x5a6f2c;
const COLOR_PINO = 0x2e5a32;
const COLOR_PINO_TRONCO = 0x3b2412;
const COLOR_CASA_PARED = 0xc9a36b;
const COLOR_CASA_TECHO = 0x6b3a1f;
const COLOR_CASA_PUERTA = 0x4a2a14;
const COLOR_CASA_VENTANA = 0x88a8d8;
const COLOR_CASA_VENTANA_OSCURA = 0x3a4560;
const COLOR_CASA_PROHIBIDA = 0x8a3324; // rojo apagado para casas prohibidas
const COLOR_PANEL = 0x22222e;
const COLOR_PANEL_PROHIBIDO = 0x3a1a18; // rojo oscuro para el aviso
const COLOR_PANEL_DETENCION = 0x1a2a48; // azul policía para la detención
const COLOR_BOTON = 0x2a2a38;
const COLOR_BOTON_HOVER = 0x38384a;
const COLOR_BOTON_PELIGRO = 0xc0392b;
const COLOR_BOTON_PELIGRO_HOVER = 0xe74c3c;
const COLOR_BOTON_DESTACADO = 0xc0392b;
const COLOR_BOTON_DESTACADO_HOVER = 0xe74c3c;
const COLOR_BORDE_PANEL = 0x38384a;
const COLOR_BORDE_PELIGRO = 0x883028;
const COLOR_TEXTO = '#ffffff';
const COLOR_TEXTO_DIM = '#8a8a95';
const COLOR_TEXTO_DESTACADO = '#fff8e1';

const HUD_ALTO = 100;
const MARGEN = 24;

/** Modos del panel central. La escena cicla entre ellos según lo que
 *  toque el jugador. `casaId` se conserva en aviso/detención para saber
 *  a qué casa volver si fuera necesario, pero en esta escena siempre
 *  volvemos al panel de zona (no navegamos hacia la casa). */
type PanelMode =
  | { tipo: 'zona' }
  | { tipo: 'aviso'; casaId: 'ventura' | 'bayonga' }
  | { tipo: 'detencion'; multa: number };

export class MonteScene extends Phaser.Scene {
  /** Key pública — el orquestador la registra en nav.ts. */
  public static readonly KEY = 'MonteScene';

  private ambienteContainer!: Phaser.GameObjects.Container;
  private uiContainer!: Phaser.GameObjects.Container;

  /** Estado actual del panel central. Inicia en 'zona'. */
  private panelMode: PanelMode = { tipo: 'zona' };

  constructor() {
    super(MonteScene.KEY);
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLOR_FONDO_CIELO);

    ensureRunning(this, SCENE_KEYS.HUD);
    this.scene.bringToTop(SCENE_KEYS.HUD);

    this.ambienteContainer = this.add.container(0, 0);
    this.uiContainer = this.add.container(0, 0);

    this.dibujarAmbiente(width, height);
    this.renderUI(width, height);
  }

  // ---------------------------------------------------------------------
  // Ambiente: camino de tierra entre pinos, con 3 casas a distintas alturas
  // ---------------------------------------------------------------------

  private dibujarAmbiente(width: number, height: number): void {
    // Cielo (tarde, banda cálida baja).
    const cielo = this.add.graphics();
    cielo.fillStyle(COLOR_FONDO_CIELO, 1);
    cielo.fillRect(0, 0, width, height * 0.6);
    cielo.fillStyle(COLOR_CIELO_BAJO, 0.85);
    cielo.fillRect(0, height * 0.42, width, height * 0.2);
    this.ambienteContainer.add(cielo);

    // Línea de pinos al fondo (silueta).
    const pinos = this.add.graphics();
    pinos.fillStyle(COLOR_PINO, 1);
    for (let i = 0; i < 14; i++) {
      const px = (width / 14) * i + 6;
      const py = height * 0.34 + (i % 2 === 0 ? 0 : 10);
      pinos.fillTriangle(px, py, px + 16, py + 70, px + 32, py);
    }
    this.ambienteContainer.add(pinos);

    // Suelo (tierra + un poco de hierba alrededor).
    const suelo = this.add.graphics();
    suelo.fillStyle(COLOR_SUELO, 1);
    suelo.fillRect(0, height * 0.55, width, height * 0.45);
    this.ambienteContainer.add(suelo);

    const cesped = this.add.graphics();
    cesped.fillStyle(COLOR_HIERBA, 0.7);
    cesped.fillRect(0, height * 0.55, width, 8);
    this.ambienteContainer.add(cesped);

    // Camino de tierra entre las casas (rectángulo más claro en el centro).
    const camino = this.add.graphics();
    camino.fillStyle(COLOR_HIERBA, 0.4);
    camino.fillRect(width * 0.42, height * 0.55, width * 0.16, height * 0.42);
    this.ambienteContainer.add(camino);

    // 3 casas dibujadas a distinta altura para ambient. Posiciones X/Y
    // aproximadas — la interactividad real está en los 3 paneles del UI.
    // Casa del VENTURA (izquierda, abajo).
    this.dibujarCasita(width * 0.2, height * 0.65, 0.95, { prohibida: true });
    // Casa del BAYONGA (derecha, abajo).
    this.dibujarCasita(width * 0.8, height * 0.62, 1.0, { prohibida: true });
    // Casa RANDOM (centro, arriba — al fondo del camino).
    this.dibujarCasita(width * 0.5, height * 0.55, 0.7, { prohibida: false });

    // Pinos sueltos delante de las casas para profundidad.
    const pinoFrontal = this.add.graphics();
    pinoFrontal.fillStyle(COLOR_PINO_TRONCO, 1);
    pinoFrontal.fillRect(width * 0.05, height * 0.7, 6, 60);
    pinoFrontal.fillStyle(COLOR_PINO, 1);
    pinoFrontal.fillTriangle(width * 0.05 - 14, height * 0.72, width * 0.05 + 20, height * 0.72, width * 0.05 + 3, height * 0.6);
    this.ambienteContainer.add(pinoFrontal);

    const pinoFrontal2 = this.add.graphics();
    pinoFrontal2.fillStyle(COLOR_PINO_TRONCO, 1);
    pinoFrontal2.fillRect(width * 0.93, height * 0.72, 6, 70);
    pinoFrontal2.fillStyle(COLOR_PINO, 1);
    pinoFrontal2.fillTriangle(width * 0.93 - 14, height * 0.74, width * 0.93 + 20, height * 0.74, width * 0.93 + 3, height * 0.6);
    this.ambienteContainer.add(pinoFrontal2);
  }

  /**
   * Dibuja una casita genérica de fondo (solo ambient, no interactiva).
   * @param cx centro X
   * @param cy base Y (suelo donde se asienta)
   * @param escala tamaño relativo (1 = base ~110px alto)
   * @param prohibida si true, pinta un cartel "PROHIBIDO" en la fachada
   *                 (rojo apagado) para vender que "no hay que entrar".
   */
  private dibujarCasita(
    cx: number,
    cy: number,
    escala: number,
    opts: { prohibida: boolean },
  ): void {
    const ancho = 80 * escala;
    const alto = 60 * escala;

    // Cuerpo de la casa.
    const cuerpo = this.add.graphics();
    cuerpo.fillStyle(COLOR_CASA_PARED, 1);
    cuerpo.fillRect(cx - ancho / 2, cy - alto, ancho, alto);
    cuerpo.lineStyle(2, COLOR_CASA_TECHO, 1);
    cuerpo.strokeRect(cx - ancho / 2, cy - alto, ancho, alto);
    this.ambienteContainer.add(cuerpo);

    // Tejado triangular.
    const tejado = this.add.graphics();
    tejado.fillStyle(COLOR_CASA_TECHO, 1);
    tejado.fillTriangle(
      cx - ancho / 2 - 6 * escala,
      cy - alto,
      cx + ancho / 2 + 6 * escala,
      cy - alto,
      cx,
      cy - alto - 24 * escala,
    );
    this.ambienteContainer.add(tejado);

    // Puerta.
    const puerta = this.add.graphics();
    puerta.fillStyle(COLOR_CASA_PUERTA, 1);
    puerta.fillRect(cx - 8 * escala, cy - 26 * escala, 16 * escala, 26 * escala);
    this.ambienteContainer.add(puerta);

    // Ventana iluminada (si prohibida: oscura).
    const ventana = this.add.graphics();
    ventana.fillStyle(opts.prohibida ? COLOR_CASA_VENTANA_OSCURA : COLOR_CASA_VENTANA, 1);
    ventana.fillRect(cx + ancho / 2 - 18 * escala, cy - alto + 10 * escala, 12 * escala, 12 * escala);
    this.ambienteContainer.add(ventana);

    // Cartel "PROHIBIDO" en la fachada si toca (solo decorativo, no interactivo).
    if (opts.prohibida) {
      const cartel = this.add.graphics();
      const cxCartel = cx + ancho / 2 - 30 * escala;
      const cyCartel = cy - alto - 12 * escala;
      cartel.fillStyle(COLOR_CASA_PROHIBIDA, 1);
      cartel.fillRoundedRect(
        cxCartel - 16 * escala,
        cyCartel - 6 * escala,
        32 * escala,
        12 * escala,
        2,
      );
      this.ambienteContainer.add(cartel);
      this.ambienteContainer.add(
        this.add
          .text(cxCartel, cyCartel, '⛔', {
            fontFamily: 'system-ui, sans-serif',
            fontSize: `${Math.round(11 * escala)}px`,
          })
          .setOrigin(0.5),
      );
    }
  }

  // ---------------------------------------------------------------------
  // UI: panel ambient + 3 casas / aviso / detención + volver
  // ---------------------------------------------------------------------

  private renderUI(width: number, height: number): void {
    this.limpiarUI();

    // El panel ambient + el botón volver se mantienen siempre.
    if (this.panelMode.tipo === 'zona') {
      this.renderPanelAmbient(width);
      this.renderCasas(width, height);
    } else if (this.panelMode.tipo === 'aviso') {
      this.renderPanelAviso(this.panelMode.casaId, width, height);
    } else if (this.panelMode.tipo === 'detencion') {
      this.renderPanelDetencion(this.panelMode.multa, width, height);
    }

    this.renderBotonVolver(width, height);
  }

  private limpiarUI(): void {
    if (!this.uiContainer) return;
    this.uiContainer.removeAll(true);
  }

  /** Panel ambient (modo zona): nombre de zona + descripción corta. */
  private renderPanelAmbient(width: number): void {
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 110;
    const y = HUD_ALTO + 60;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 0.92);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.setPosition(width / 2, y);
    this.uiContainer.add(panel);

    this.uiContainer.add(
      this.add
        .text(width / 2 - panelAncho / 2 + 50, y - 16, DESCRIPCION_MONTE.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '34px',
        })
        .setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add.text(width / 2 - panelAncho / 2 + 96, y - 22, DESCRIPCION_MONTE.nombre, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: COLOR_TEXTO_DESTACADO,
      }),
    );
    this.uiContainer.add(
      this.add.text(width / 2 - 8, y + 16, DESCRIPCION_MONTE.ambient, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: COLOR_TEXTO,
        align: 'left',
        wordWrap: { width: panelAncho - 150 },
      }),
    );
  }

  /** Las 3 casas del monte como paneles tocables (modo zona). */
  private renderCasas(width: number, height: number): void {
    // 3 paneles verticales, debajo del panel ambient y antes del volver.
    const areaTop = HUD_ALTO + 200;
    const areaBottom = height - 110;
    const gap = 12;
    const panelAncho = width - MARGEN * 2;
    const panelAlto = (areaBottom - areaTop - gap * 2) / 3;

    const ids: CasaMonteId[] = ['ventura', 'bayonga', 'random'];
    ids.forEach((id, i) => {
      const y = areaTop + i * (panelAlto + gap) + panelAlto / 2;
      if (id === 'random') {
        this.renderCasaRandom(width / 2, y, panelAncho, panelAlto);
      } else {
        // id se ha estrechado a 'ventura' | 'bayonga'.
        this.renderCasaProhibida(id, width / 2, y, panelAncho, panelAlto);
      }
    });
  }

  /** Panel de una casa prohibida (VENTURA / BAYONGA). Tocarla abre el aviso. */
  private renderCasaProhibida(
    casaId: 'ventura' | 'bayonga',
    x: number,
    y: number,
    ancho: number,
    alto: number,
  ): void {
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(x, y);
    this.uiContainer.add(boton);

    // Emoji izquierda.
    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 30, y, '⛔', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '34px',
        })
        .setOrigin(0, 0.5),
    );

    // Título (etiqueta de la casa) + subtítulo prohibido.
    this.uiContainer.add(
      this.add.text(x - ancho / 2 + 78, y - 10, ETIQUETAS_CASAS_MONTE[casaId], {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
      }),
    );
    this.uiContainer.add(
      this.add.text(x - ancho / 2 + 78, y + 14, 'PROHIBIDO — no entrar', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: COLOR_TEXTO_DIM,
      }),
    );

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => {
      this.panelMode = { tipo: 'aviso', casaId };
      this.renderUI(this.scale.width, this.scale.height);
    });
    this.uiContainer.add(zona);
  }

  /** Panel de la CASA RANDOM. Si la misión está completada, muestra
   *  texto "Ya le disteis un buen repaso ✅" en vez del botón
   *  "Inspeccionar la casa random" (que sí lanza la misión). */
  private renderCasaRandom(x: number, y: number, ancho: number, alto: number): void {
    const completada = GameState.misionCompletada('la-casa-random');

    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(completada ? COLOR_BOTON : COLOR_BOTON_DESTACADO);
    boton.setPosition(x, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 30, y, '🏚️', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '34px',
        })
        .setOrigin(0, 0.5),
    );

    if (completada) {
      // Sin botón: solo texto "ya completado".
      this.uiContainer.add(
        this.add.text(x - ancho / 2 + 78, y - 8, ETIQUETAS_CASAS_MONTE.random, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
        }),
      );
      this.uiContainer.add(
        this.add.text(x - ancho / 2 + 78, y + 14, CASA_RANDOM_COMPLETADA, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: COLOR_TEXTO_DIM,
          wordWrap: { width: ancho - 100 },
        }),
      );
      // Zona no interactiva (decorativa) para no robar taps al volver.
      this.uiContainer.add(this.add.zone(x, y, ancho, alto));
      return;
    }

    // Misión no completada: botón destacado "Inspeccionar la casa random".
    this.uiContainer.add(
      this.add.text(x - ancho / 2 + 78, y - 8, ETIQUETAS_CASAS_MONTE.random, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        fontStyle: 'bold',
        color: COLOR_TEXTO_DESTACADO,
      }),
    );
    this.uiContainer.add(
      this.add.text(x - ancho / 2 + 78, y + 14, BOTON_INSPECCIONAR_CASA_RANDOM, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#fff8e1',
        wordWrap: { width: ancho - 100 },
      }),
    );

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_DESTACADO_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON_DESTACADO));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_DESTACADO_HOVER));
    zona.on('pointerup', () => {
      const data: MissionSceneData = { missionId: 'la-casa-random' };
      goTo(this, SCENE_KEYS.MISSION, data);
    });
    this.uiContainer.add(zona);
  }

  /** Panel AVISO PROHIBIDO — al tocar una casa prohibida. 2 botones:
   *  "Acercarse igualmente…" (peligro, abre la detención) y
   *  "Sí, mejor dar media vuelta" (vuelve al panel de zona). */
  private renderPanelAviso(casaId: 'ventura' | 'bayonga', width: number, height: number): void {
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 380;
    const cx = width / 2;
    const cy = height * 0.36;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL_PROHIBIDO, 0.96);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.lineStyle(2, COLOR_BORDE_PELIGRO, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.setPosition(cx, cy);
    this.uiContainer.add(panel);

    // Emoji enorme arriba.
    this.uiContainer.add(
      this.add
        .text(cx, cy - panelAlto / 2 + 60, AVISO_PROHIBIDO.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '60px',
        })
        .setOrigin(0.5),
    );
    // Título.
    this.uiContainer.add(
      this.add.text(cx, cy - panelAlto / 2 + 130, AVISO_PROHIBIDO.titulo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: '#ffb4a8',
        align: 'center',
        wordWrap: { width: panelAncho - 40 },
      }).setOrigin(0.5),
    );
    // Texto (menciona el apodo del dueño de la casa).
    this.uiContainer.add(
      this.add.text(cx, cy + 10, AVISO_PROHIBIDO.texto, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: COLOR_TEXTO,
        align: 'center',
        wordWrap: { width: panelAncho - 50 },
      }).setOrigin(0.5),
    );

    // Botón "Acercarse igualmente…" (peligro).
    this.crearBotonRect(
      cx,
      cy + panelAlto / 2 - 110,
      panelAncho - 60,
      56,
      BOTON_INSISTIR,
      COLOR_BOTON_PELIGRO,
      COLOR_BOTON_PELIGRO_HOVER,
      () => {
        // Canon: pierde el 20% del dinero (multa) al insistir.
        const multa = Math.floor(GameState.dinero * MULTA_PORCENTAJE);
        // gastarDinero(0) siempre devuelve true porque dinero >= 0.
        if (multa > 0) {
          GameState.gastarDinero(multa);
        }
        this.panelMode = { tipo: 'detencion', multa };
        this.renderUI(width, height);
      },
    );

    // Botón "Sí, mejor dar media vuelta" — vuelve al panel de zona.
    this.crearBotonRect(
      cx,
      cy + panelAlto / 2 - 36,
      panelAncho - 60,
      50,
      BOTON_VOLVER_AVISO,
      COLOR_BOTON,
      COLOR_BOTON_HOVER,
      () => {
        this.panelMode = { tipo: 'zona' };
        this.renderUI(width, height);
      },
    );

    // (sanity check: `casaId` no se usa para enrutar — siempre volvemos
    // al panel de zona — pero lo conservamos por si en un bloque futuro
    // interesa saber de qué casa venía el jugador.)
    void casaId;
  }

  /** Panel DETENCIÓN — el jugador insistió, perdió dinero, ahora ve el
   *  "Busted" y vuelve al panel de zona con el botón "OK, me las piro". */
  private renderPanelDetencion(multa: number, width: number, height: number): void {
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 360;
    const cx = width / 2;
    const cy = height * 0.36;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL_DETENCION, 0.96);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.lineStyle(2, 0x2a4080, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.setPosition(cx, cy);
    this.uiContainer.add(panel);

    this.uiContainer.add(
      this.add
        .text(cx, cy - panelAlto / 2 + 64, DETENCION.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '60px',
        })
        .setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add.text(cx, cy - panelAlto / 2 + 130, DETENCION.titulo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        fontStyle: 'bold',
        color: '#a8c8ff',
        align: 'center',
        wordWrap: { width: panelAncho - 40 },
      }).setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add.text(cx, cy - 8, DETENCION.texto, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: COLOR_TEXTO,
        align: 'center',
        wordWrap: { width: panelAncho - 50 },
      }).setOrigin(0.5),
    );

    // Línea de multa dinámica.
    const multaLinea = DETENCION.multaLinea.replace('{multa}', String(multa));
    this.uiContainer.add(
      this.add.text(cx, cy + 70, multaLinea, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        fontStyle: 'bold',
        color: '#ffb4a8',
        align: 'center',
        wordWrap: { width: panelAncho - 50 },
      }).setOrigin(0.5),
    );

    // Botón "OK, me las piro".
    this.crearBotonRect(
      cx,
      cy + panelAlto / 2 - 50,
      panelAncho - 60,
      60,
      BOTON_OK_DETENCION,
      COLOR_BOTON,
      COLOR_BOTON_HOVER,
      () => {
        this.panelMode = { tipo: 'zona' };
        this.renderUI(width, height);
      },
    );
  }

  /** Helper para un botón rectangular centrado en (x, y) con texto. */
  private crearBotonRect(
    x: number,
    y: number,
    ancho: number,
    alto: number,
    texto: string,
    colorNormal: number,
    colorHover: number,
    onUp: () => void,
  ): void {
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(colorNormal);
    boton.setPosition(x, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(x, y, texto, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '17px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: ancho - 30 },
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(colorHover));
    zona.on('pointerout', () => dibujar(colorNormal));
    zona.on('pointerdown', () => dibujar(colorHover));
    zona.on('pointerup', () => onUp());
    this.uiContainer.add(zona);
  }

  /** Botón "← Volver al barrio" siempre presente (también cuando hay
   *  aviso o detención — el jugador puede salir del flujo). */
  private renderBotonVolver(width: number, height: number): void {
    const ancho = 260;
    const alto = 56;
    const y = height - 30;

    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(width / 2, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(width / 2, y, BOTON_VOLVER_BARRIO, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(width / 2, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => {
      goTo(this, SCENE_KEYS.HUB);
    });
    this.uiContainer.add(zona);
  }
}
